import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import { getCurrencyInfo } from '../utils/currencyService';
import './EditPlan.css';

const EditPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [currencySymbol, setCurrencySymbol] = useState('£');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    duration: '',
    price: '',
    description: ''
  });

  useEffect(() => {
    // Initialize currency
    const initializeCurrency = async () => {
      try {
        const currencyInfo = await getCurrencyInfo();
        setCurrencySymbol(currencyInfo.symbol);
      } catch (error) {
        console.error('Error fetching currency:', error);
        setCurrencySymbol('£'); // Default fallback
      }
    };

    initializeCurrency();

    const fetchPlan = async () => {
      try {
        const response = await axios.get(createApiUrl(`/api/plans/${id}`));
        setPlan(response.data);
        setFormData({
          name: response.data.name,
          duration: response.data.duration,
          price: response.data.price,
          description: response.data.description
        });
      } catch (error) {
        console.error('Error fetching plan:', error);
      }
    };

    fetchPlan();

    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setMessageType('');
    
    try {
      await axios.post(createApiUrl(`/api/plan/${id}`), formData);
      
      // Show success message
      setMessage('Plan updated successfully!');
      setMessageType('success');
      
      // Redirect back to the All Plans tab after a short delay
      setTimeout(() => {
        navigate('/admin-dashboard?tab=plans');
      }, 1500);
    } catch (error) {
      console.error('Error updating plan:', error);
      
      // Show error message
      setMessage('Failed to update plan. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!plan) {
    return <div>Loading...</div>;
  }

  return (
    <div className="edit-plan-container">
      {!isOnline && (
        <div className="connection-status">You are currently offline</div>
      )}

      <h1 className="edit-title">Edit Plan</h1>

      {message && (
        <div className={`message ${messageType === 'success' ? 'message-success' : 'message-error'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} id="edit-form" className="form">
        <div className="form-content">
          <div className="form-group">
            <label htmlFor="name" className="form-label">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-control"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="duration" className="form-label">Duration (days):</label>
            <input
              type="number"
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              className="form-control"
              min="1"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="price" className="form-label">Price ({currencySymbol}):</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="form-control"
              min="0"
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">Description:</label>
            <textarea
              name="description"
              id="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="8"
              className="form-control preserve-whitespace"
              required
            />
          </div>
        </div>

        <div className="button-group">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Plan'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPlan; 